import { isDevelopment } from '@/lib/config';
import { registerSuperProperties } from '@/lib/mixpanel';

/**
 * Real-time transport resolution.
 *
 * Outside production the SSE deal-events channel is the default, so localhost,
 * the ephemeral CI preview channels and `deals.appchoose.co` all run on SSE
 * without any URL param. Production (`deals.appchoose.io`) still defaults to the
 * GraphQL WebSocket subscriptions, with SSE opt-in via `?mode=sse`.
 *
 * `?mode=sse` / `?mode=ws` force a transport and **latch** it for the rest of the
 * browser-tab session (`sessionStorage`), so the choice survives client-side
 * navigations that drop the query param; a fresh tab falls back to the
 * environment default. Any other `?mode` value is ignored and does not latch.
 *
 * This lives in its own module (not `config.ts`) so the super-property helper's
 * Mixpanel import doesn't create a config -> mixpanel import cycle.
 */

export type DealRealtimeTransport = 'sse' | 'ws';

const SESSION_MODE_KEY = 'dealRealtimeMode';

function isTransport(value: string | null): value is DealRealtimeTransport {
  return value === 'sse' || value === 'ws';
}

function readSessionMode(): DealRealtimeTransport | null {
  try {
    const stored = window.sessionStorage.getItem(SESSION_MODE_KEY);
    return isTransport(stored) ? stored : null;
  } catch {
    return null;
  }
}

function latchSessionMode(mode: DealRealtimeTransport): void {
  try {
    window.sessionStorage.setItem(SESSION_MODE_KEY, mode);
  } catch {
    /* sessionStorage unavailable (private mode, quota, …) — the URL param still
       drives the current view, it just won't stick across navigations. */
  }
}

/**
 * Resolve the real-time transport for the current view, in priority order:
 * explicit `?mode=` param (latching), then the session latch, then the
 * environment default (SSE everywhere but production).
 */
export function dealRealtimeTransport(): DealRealtimeTransport {
  if (typeof window === 'undefined') return 'ws';

  const urlMode = new URLSearchParams(window.location.search).get('mode');
  if (isTransport(urlMode)) {
    latchSessionMode(urlMode);
    return urlMode;
  }

  return readSessionMode() ?? (isDevelopment ? 'sse' : 'ws');
}

/** Whether the SSE deal-events channel replaces the WebSocket subscriptions. */
export function isDealEventsSseEnabled(): boolean {
  return dealRealtimeTransport() === 'sse';
}

/**
 * Register the resolved real-time transport as a Mixpanel super property so
 * every event (SSE/WS lifecycle and all others) can be segmented by which
 * transport the user is on. No-op until Mixpanel is initialized.
 */
export function registerDealRealtimeTransportProperty(): void {
  registerSuperProperties({ dealRealtimeTransport: dealRealtimeTransport() });
}
